import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  Close,
  Edit,
  Pause,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getCategoryColor, CATEGORIES } from '../utils/categoryColors';
import { formatCurrency, getMonthlyCostInCurrency } from '../utils/currency';
import { useUserPreferences } from '../hooks/useUserPreferences';
import toast from 'react-hot-toast';
import ServiceLogo from './ServiceLogo';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import { SUPPORTED_CURRENCIES } from '../utils/currency';
import { BILLING_CYCLES } from '../utils/billingCycleUtils';
import { formatBillingCycleForDisplay } from '../utils/billingCycleUtils';

interface Subscription {
  id: string;
  name: string;
  cost: number;
  billing_cycle: string;
  category: string;
  payment_method: string;
  notes?: string;
  next_renewal_date: string;
  is_active: boolean;
  is_trial?: boolean;
  trial_end_date?: string;
  currency?: string;
}

interface CalendarDateModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  subscriptions: Subscription[];
  onSubscriptionUpdate: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`calendar-tabpanel-${index}`}
      aria-labelledby={`calendar-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CalendarDateModal: React.FC<CalendarDateModalProps> = ({
  open,
  onClose,
  selectedDate,
  subscriptions,
  onSubscriptionUpdate,
}) => {
  const { user } = useAuth();
  const { getMainCurrency } = useUserPreferences(user?.id);
  const { getPaymentMethodNames } = usePaymentMethods(user?.id);
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [deactivatingSubscription, setDeactivatingSubscription] = useState<Subscription | null>(null);
  const [showDateWarning, setShowDateWarning] = useState(false);
  const [showDeactivateWarning, setShowDeactivateWarning] = useState(false);

  const [editForm, setEditForm] = useState({
    name: '',
    cost: 0,
    billing_cycle: 'monthly',
    category: 'Entertainment',
    payment_method: '',
    notes: '',
    next_renewal_date: '',
    currency: getMainCurrency(), // Initialize with user's main currency
  });

  const [newSubscription, setNewSubscription] = useState({
    name: '',
    cost: 0,
    billing_cycle: 'monthly',
    category: 'Entertainment',
    payment_method: '',
    notes: '',
    currency: getMainCurrency(), // Initialize with user's main currency
  });

  const [newEditPaymentMethodName, setNewEditPaymentMethodName] = useState('');
  const [newAddPaymentMethodName, setNewAddPaymentMethodName] = useState('');
  const [newEditPaymentMethodType, setNewEditPaymentMethodType] = useState('credit_card');
  const [newAddPaymentMethodType, setNewAddPaymentMethodType] = useState('credit_card');

  // Reset new payment method names when payment method selections change
  useEffect(() => {
    if (editForm.payment_method !== '__add_new__') {
      setNewEditPaymentMethodName('');
      setNewEditPaymentMethodType('credit_card');
    }
  }, [editForm.payment_method]);

  useEffect(() => {
    if (newSubscription.payment_method !== '__add_new__') {
      setNewAddPaymentMethodName('');
      setNewAddPaymentMethodType('credit_card');
    }
  }, [newSubscription.payment_method]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditSubscription = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setEditForm({
      name: subscription.name,
      cost: subscription.cost,
      billing_cycle: subscription.billing_cycle,
      category: subscription.category,
      payment_method: subscription.payment_method,
      notes: subscription.notes || '',
      next_renewal_date: subscription.next_renewal_date,
      currency: subscription.currency || getMainCurrency(), // Set currency from subscription
    });
    setTabValue(1);
  };

  const handleUpdateSubscription = async () => {
    if (!editingSubscription) return;

    try {
      // Handle new payment method
      let finalPaymentMethod = editForm.payment_method;
      if (editForm.payment_method === '__add_new__') {
        if (!newEditPaymentMethodName.trim()) {
          toast.error('Please enter a payment method name');
          return;
        }
        finalPaymentMethod = newEditPaymentMethodName.trim();
        
        // Create the new payment method first
        const { error: pmError } = await supabase
          .from('payment_methods')
          .insert({
            user_id: user?.id,
            name: newEditPaymentMethodName.trim(),
            type: newEditPaymentMethodType,
            is_active: true,
          });

        if (pmError) throw pmError;
      }

      const { error } = await supabase
        .from('subscriptions')
        .update({
          name: editForm.name,
          cost: editForm.cost,
          billing_cycle: editForm.billing_cycle,
          category: editForm.category,
          payment_method: finalPaymentMethod,
          notes: editForm.notes,
          next_renewal_date: editForm.next_renewal_date,
          currency: editForm.currency, // Update currency
        })
        .eq('id', editingSubscription.id);

      if (error) throw error;

      toast.success('Subscription updated successfully!');
      setEditingSubscription(null);
      setTabValue(0);
      setNewEditPaymentMethodName('');
      setNewEditPaymentMethodType('credit_card');
      onSubscriptionUpdate();
    } catch (error) {
      toast.error('Failed to update subscription');
      console.error('Error updating subscription:', error);
    }
  };

  const handleDeactivateSubscription = async (subscription: Subscription) => {
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ is_active: false })
        .eq('id', subscription.id);

      if (error) throw error;

      toast.success('Subscription deactivated successfully!');
      onSubscriptionUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to deactivate subscription');
      console.error('Error deactivating subscription:', error);
    }
  };

  const handleAddSubscription = async () => {
    if (!selectedDate || !user) return;

    try {
      // Handle new payment method
      let finalPaymentMethod = newSubscription.payment_method;
      if (newSubscription.payment_method === '__add_new__') {
        if (!newAddPaymentMethodName.trim()) {
          toast.error('Please enter a payment method name');
          return;
        }
        finalPaymentMethod = newAddPaymentMethodName.trim();
        
        // Create the new payment method first
        const { error: pmError } = await supabase
          .from('payment_methods')
          .insert({
            user_id: user.id,
            name: newAddPaymentMethodName.trim(),
            type: newAddPaymentMethodType,
            is_active: true,
          });

        if (pmError) throw pmError;
      }

      // Get the user's main currency
      const mainCurrency = getMainCurrency();

      const { error } = await supabase
        .from('subscriptions')
        .insert({
          user_id: user.id,
          name: newSubscription.name,
          cost: newSubscription.cost,
          currency: newSubscription.currency || mainCurrency, // Use user's main currency if not specified
          billing_cycle: newSubscription.billing_cycle,
          category: newSubscription.category,
          payment_method: finalPaymentMethod,
          notes: newSubscription.notes,
          next_renewal_date: format(selectedDate, 'yyyy-MM-dd'),
          is_active: true,
        });

      if (error) throw error;

      toast.success('Subscription added successfully!');
      setNewSubscription({
        name: '',
        cost: 0,
        billing_cycle: 'monthly',
        category: 'Entertainment',
        payment_method: '',
        notes: '',
        currency: getMainCurrency(), // Reset to user's main currency
      });
      setNewAddPaymentMethodName('');
      setNewAddPaymentMethodType('credit_card');
      setTabValue(0);
      onSubscriptionUpdate();
    } catch (error) {
      toast.error('Failed to add subscription');
      console.error('Error adding subscription:', error);
    }
  };

  const handleDateChange = (newDate: string) => {
    setEditForm({ ...editForm, next_renewal_date: newDate });
    setShowDateWarning(true);
  };

  const confirmDateChange = () => {
    setShowDateWarning(false);
    // Date change is already applied in editForm
  };

  const cancelDateChange = () => {
    setEditForm({ ...editForm, next_renewal_date: editingSubscription?.next_renewal_date || '' });
    setShowDateWarning(false);
  };

  if (!selectedDate) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 600,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {format(selectedDate, 'MMMM d, yyyy')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="calendar modal tabs">
            <Tab label="Details" />
            <Tab label="Edit" />
            <Tab label="Add New" />
          </Tabs>
        </Box>

        {/* Details Tab */}
        <TabPanel value={tabValue} index={0}>
          {subscriptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No payments scheduled for this date
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Add New" to schedule a payment for this date
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Payments ({subscriptions.length})
              </Typography>
                             {subscriptions.map((subscription) => (
                 <Card key={subscription.id} sx={{ mb: 2 }}>
                   <CardContent>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <ServiceLogo
                           serviceName={subscription.name}
                           category={subscription.category}
                           size="medium"
                         />
                         <Typography variant="h6">{subscription.name}</Typography>
                       </Box>
                       <Chip
                         label={subscription.category}
                         size="small"
                         sx={{
                           backgroundColor: getCategoryColor(subscription.category),
                           color: 'white',
                         }}
                       />
                     </Box>
                    
                    <Typography variant="h5" color="primary" gutterBottom>
                      {formatCurrency(getMonthlyCostInCurrency(subscription, getMainCurrency()), getMainCurrency())}
                      {subscription.currency && subscription.currency !== getMainCurrency() && (
                        <Typography 
                          component="span" 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ ml: 1, fontSize: '0.7em' }}
                        >
                          ({formatCurrency(subscription.cost, subscription.currency)})
                        </Typography>
                      )}
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Billing Cycle
                        </Typography>
                        <Typography variant="body1">
                          {formatBillingCycleForDisplay(subscription.billing_cycle)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Method
                        </Typography>
                        <Typography variant="body1">
                          {subscription.payment_method || 'Not specified'}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    {subscription.notes && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body1">
                          {subscription.notes}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEditSubscription(subscription)}
                      >
                        Edit
                      </Button>
                                             <Button
                         size="small"
                         color="warning"
                         startIcon={<Pause />}
                         onClick={() => {
                           setDeactivatingSubscription(subscription);
                           setShowDeactivateWarning(true);
                         }}
                       >
                         Deactivate
                       </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Edit Tab */}
        <TabPanel value={tabValue} index={1}>
          {editingSubscription ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Edit Subscription
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subscription Name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Cost"
                    type="number"
                    value={editForm.cost}
                    onChange={(e) => setEditForm({ ...editForm, cost: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    helperText={`Cost in ${editForm.currency || getMainCurrency()}`}
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={editForm.currency || getMainCurrency()}
                      onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                      label="Currency"
                    >
                      {SUPPORTED_CURRENCIES.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.name} ({currency.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Billing Cycle</InputLabel>
                    <Select
                      value={editForm.billing_cycle}
                      onChange={(e) => setEditForm({ ...editForm, billing_cycle: e.target.value })}
                      label="Billing Cycle"
                    >
                      {BILLING_CYCLES.map((cycle) => (
                        <MenuItem key={cycle.value} value={cycle.value}>
                          {cycle.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      label="Category"
                    >
                      {CATEGORIES.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={editForm.payment_method}
                      onChange={(e) => setEditForm({ ...editForm, payment_method: e.target.value })}
                      label="Payment Method"
                    >
                      <MenuItem value="">
                        <em>Select a payment method</em>
                      </MenuItem>
                      {getPaymentMethodNames().map((methodName) => (
                        <MenuItem key={methodName} value={methodName}>
                          {methodName}
                        </MenuItem>
                      ))}
                      <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                        + Add New Payment Method
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {editForm.payment_method === '__add_new__' && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label="New Payment Method Name"
                          value={newEditPaymentMethodName}
                          onChange={(e) => setNewEditPaymentMethodName(e.target.value)}
                          placeholder="e.g., My MasterCard, Business PayPal"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={newEditPaymentMethodType}
                            onChange={(e) => setNewEditPaymentMethodType(e.target.value)}
                            label="Type"
                          >
                            <MenuItem value="credit_card">Credit Card</MenuItem>
                            <MenuItem value="debit_card">Debit Card</MenuItem>
                            <MenuItem value="paypal">PayPal</MenuItem>
                            <MenuItem value="e_wallet">E-Wallet</MenuItem>
                            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Next Renewal Date"
                    type="date"
                    value={editForm.next_renewal_date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
              
              {showDateWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ⚠️ Changing the date will update the renewal date. This affects future reminders.
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={confirmDateChange}>
                      Confirm
                    </Button>
                    <Button size="small" onClick={cancelDateChange} sx={{ ml: 1 }}>
                      Cancel
                    </Button>
                  </Box>
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                Select a subscription to edit
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Add New Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Add New Subscription
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subscription Name"
                value={newSubscription.name}
                onChange={(e) => setNewSubscription({ ...newSubscription, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={newSubscription.cost}
                onChange={(e) => setNewSubscription({ ...newSubscription, cost: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                helperText={`Cost in ${newSubscription.currency || getMainCurrency()}`}
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={newSubscription.currency || getMainCurrency()}
                  onChange={(e) => setNewSubscription({ ...newSubscription, currency: e.target.value })}
                  label="Currency"
                >
                  {SUPPORTED_CURRENCIES.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={newSubscription.billing_cycle}
                  onChange={(e) => setNewSubscription({ ...newSubscription, billing_cycle: e.target.value })}
                  label="Billing Cycle"
                >
                  {BILLING_CYCLES.map((cycle) => (
                    <MenuItem key={cycle.value} value={cycle.value}>
                      {cycle.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newSubscription.category}
                  onChange={(e) => setNewSubscription({ ...newSubscription, category: e.target.value })}
                  label="Category"
                >
                  {CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={newSubscription.payment_method}
                  onChange={(e) => setNewSubscription({ ...newSubscription, payment_method: e.target.value })}
                  label="Payment Method"
                >
                  <MenuItem value="">
                    <em>Select a payment method</em>
                  </MenuItem>
                  {getPaymentMethodNames().map((methodName) => (
                    <MenuItem key={methodName} value={methodName}>
                      {methodName}
                    </MenuItem>
                  ))}
                  <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                    + Add New Payment Method
                  </MenuItem>
                </Select>
              </FormControl>
              {newSubscription.payment_method === '__add_new__' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="New Payment Method Name"
                      value={newAddPaymentMethodName}
                      onChange={(e) => setNewAddPaymentMethodName(e.target.value)}
                      placeholder="e.g., My MasterCard, Business PayPal"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={newAddPaymentMethodType}
                        onChange={(e) => setNewAddPaymentMethodType(e.target.value)}
                        label="Type"
                      >
                        <MenuItem value="credit_card">Credit Card</MenuItem>
                        <MenuItem value="debit_card">Debit Card</MenuItem>
                        <MenuItem value="paypal">PayPal</MenuItem>
                        <MenuItem value="e_wallet">E-Wallet</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={newSubscription.notes}
                onChange={(e) => setNewSubscription({ ...newSubscription, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {tabValue === 1 && editingSubscription && (
          <Button 
            onClick={handleUpdateSubscription} 
            variant="contained"
            sx={{
              color: theme.palette.mode === 'dark' ? 'black' : 'white',
              '&:hover': {
                color: theme.palette.mode === 'dark' ? 'black' : 'white',
              },
            }}
          >
            Update Subscription
          </Button>
        )}
        {tabValue === 2 && (
          <Button 
            onClick={handleAddSubscription} 
            variant="contained"
            sx={{
              color: theme.palette.mode === 'dark' ? 'black' : 'white',
              '&:hover': {
                color: theme.palette.mode === 'dark' ? 'black' : 'white',
              },
            }}
          >
            Add Subscription
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Deactivate Warning Dialog */}
      <Dialog open={showDeactivateWarning} onClose={() => setShowDeactivateWarning(false)}>
        <DialogTitle>Deactivate Subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              ⚠️ Deactivating will remove this subscription from the calendar. You can find it in All Subscriptions.
            </Typography>
          </Alert>
          <Typography variant="body2">
            Are you sure you want to deactivate this subscription?
          </Typography>
        </DialogContent>
                 <DialogActions>
           <Button onClick={() => setShowDeactivateWarning(false)}>Cancel</Button>
           <Button 
             onClick={() => {
               if (deactivatingSubscription) {
                 handleDeactivateSubscription(deactivatingSubscription);
               }
               setShowDeactivateWarning(false);
             }} 
             color="warning"
             variant="contained"
           >
             Deactivate
           </Button>
         </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CalendarDateModal;
